import { batch, bind, createRef, h, signal, tag } from "omi";
import isEmpty from "lodash-es/isEmpty";

import { NWIcon } from "@/components/atoms/nw-icon";
import { NWTooltip } from "@/components/atoms/nw-tooltip";
import { NWNodeView } from "@/editor/utils/nodeView";
import { Store } from "@/store";
import { tailwind } from "@/tailwind";

export type NWVariableProps = {
  id: string;
  name: string;
  scope: string;
  placeholder?: string;
  variableValue?: string;
};

export type NWVariableViewActions = {
  saveVariable: (value: string) => void;
};

@tag("nw-variable")
export class NWVariable extends NWNodeView<NWVariableProps, NWVariableViewActions> {
  static css = [tailwind];

  private isEditing = signal(false);
  private inputRef = createRef<HTMLInputElement>();
  private value = signal<string | undefined>(undefined);
  private componentRef = createRef<HTMLDivElement>();
  private hasChanged = false;

  static propTypes = {
    id: String,
    name: String,
    scope: String,
    placeholder: String,
  };

  static defaultProps = {
    scope: "project",
  };

  installed() {
    if (this.isEditing.value) {
      this.focusInput();
      this.addEventListeners();
    }
  }

  uninstall() {
    if (this.isEditing.value) {
      this.removeEventListeners();
    }
  }

  receiveProps() {
    if (this.isEditing.value) {
      this.focusInput();
    }
  }

  getVariableValue(userValueOnly: boolean = false): string | undefined {
    const variable = Store.project.variables.get(this.props.name);
    if (!isEmpty(variable) || userValueOnly) {
      return variable;
    }

    return this.props.placeholder ?? this.props.name;
  }

  private focusInput() {
    setTimeout(() => {
      this.inputRef.current?.focus();
    }, 0);
  }

  private addEventListeners() {
    document.addEventListener("mousedown", this.handleOutsideClick);
    document.addEventListener("keydown", this.handleKeyDown);
  }

  private removeEventListeners() {
    document.removeEventListener("mousedown", this.handleOutsideClick);
    document.removeEventListener("keydown", this.handleKeyDown);
  }

  @bind
  private handleOutsideClick(event: MouseEvent) {
    const path = event.composedPath();

    if (path.includes(this) || path.some(el => el instanceof Node && this.contains(el as Node))) {
      return;
    }

    this.exitEditing();
  }

  @bind
  private handleKeyDown(event: KeyboardEvent) {
    // Exit editing mode on Escape key
    if (event.key === "Escape") {
      event.preventDefault();
      this.exitEditing();
    }
  }

  private exitEditing() {
    this.removeEventListeners();
    batch(() => {
      this.isEditing.value = false;
      this.value.value = undefined;
      this.hasChanged = false;
    });
  }

  @bind
  private toggleEditing(e: Event, shouldOpen: boolean) {
    e.preventDefault();

    if (shouldOpen) {
      batch(() => {
        this.value.value = this.getVariableValue(true);
        this.isEditing.value = true;
        this.focusInput();
      });
      this.addEventListeners();
    } else {
      this.exitEditing();
    }
  }

  @bind
  private handleInput(event: Event) {
    const value = (event.target as HTMLInputElement).value;

    if (value != this.value.value) {
      this.hasChanged = true;
    }

    this.value.value = value;
  }

  @bind
  private handleSubmit(event: Event) {
    if (!this.hasChanged) {
      this.toggleEditing(event, false);
      return;
    }

    event.preventDefault();
    this.isEditing.value = false;
    Store.project.variables.set(this.props.name, this.value.value);
    this.viewActions.saveVariable(this.value.value ?? "");
  }

  renderEditing() {
    return (
      <form
        className="relative h-8 inline-flex overflow-hidden items-center"
        onSubmit={this.handleSubmit}
        ref={this.componentRef}
      >
        <input
          type="text"
          ref={this.inputRef}
          class="w-fit outline-none rounded-md px-2 pr-14 flex-none h-[1.7rem] min-h-[1.7rem] border leading-4 text-primary align-middle box-border"
          onInput={this.handleInput}
          value={this.value.value}
          placeholder={this.props.placeholder}
        />
        <div className="absolute right-2 top-1/2 transform -translate-y-1/2 flex gap-1">
          <NWIcon
            name="gray-check"
            class="text-brand-500 cursor-pointer hover:text-brand-600"
            width={16}
            height={16}
            onClick={this.handleSubmit}
          />
          <NWIcon
            name="cross"
            class="text-brand-500 cursor-pointer hover:text-brand-600"
            width={16}
            height={16}
            onClick={e => this.toggleEditing(e, false)}
          />
        </div>
      </form>
    );
  }

  renderDisplay() {
    return (
      <div class="cursor-pointer h-8 bg-brand-100 hover:bg-brand-200 transition-bg px-2 py-1 rounded-md w-fit inline-block">
        <NWTooltip
          content="Edit variable"
          placement="top"
          class="inline-flex"
          onClick={e => this.toggleEditing(e, true)}
        >
          <span class="inline-flex items-center gap-1 font-semibold w-fit">
            <span class="truncate max-w-[34ch]">{this.getVariableValue()}</span>
            <NWIcon name="pencil" class="text-gray-700" width={16} height={16} />
          </span>
        </NWTooltip>
      </div>
    );
  }

  render() {
    return this.isEditing.value ? this.renderEditing() : this.renderDisplay();
  }
}
